import { useEffect, useRef, useState } from 'react';

const VERSION_URL = '/version.json';
const POLL_INTERVAL_MS = 5 * 60 * 1000;
const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1'];

/**
 * Polls the statically-served version.json (regenerated on every deploy) and
 * flags when it no longer matches the version baked into the currently
 * running bundle, so long-lived tabs can be nudged to refresh after a deploy.
 */
export const useAppVersionCheck = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const buildVersion = useRef(process.env.APP_VERSION);

  useEffect(() => {
    // Skip on the local dev server: there's no deploy to detect there.
    // Note: `process.env.mode` isn't a reliable signal for this — QA and
    // stage webpack configs both (mis)label it 'dev' — so check the actual
    // hostname instead of trusting that flag.
    if (
      !buildVersion.current ||
      LOCALHOST_HOSTNAMES.includes(window.location.hostname)
    )
      return;

    let cancelled = false;

    const checkVersion = async () => {
      try {
        const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
          cache: 'no-store',
        });
        if (!response.ok) return;
        const data = await response.json();
        if (
          !cancelled &&
          data?.version &&
          data.version !== buildVersion.current
        ) {
          setUpdateAvailable(true);
          stopPolling();
        }
      } catch (_err) {
        // Ignore network errors; the next poll/focus check will retry.
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkVersion();
    };

    const stopPolling = () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkVersion);
    };

    checkVersion();
    const intervalId = setInterval(checkVersion, POLL_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkVersion);

    return () => {
      cancelled = true;
      stopPolling();
    };
  }, []);

  return updateAvailable;
};
